import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.400'),
		paddingBlockEnd: token('space.400'),
		textAlign: 'center',
	},
	illustration: {
		marginBlockEnd: token('space.300'),
	},
});

const messages = defineMessages({
	heading: {
		id: 'rovo-spaces.recommended-spaces-empty-state.heading',
		defaultMessage: "There's nothing to review right now",
		description: 'Heading shown in the empty state when there are no recommended spaces',
	},
	body: {
		id: 'rovo-spaces.recommended-spaces-empty-state.body',
		defaultMessage: 'Check again later for new recommendations.',
		description: 'Body text shown in the empty state when there are no recommended spaces',
	},
	createSpace: {
		id: 'rovo-spaces.recommended-spaces-empty-state.create-space',
		defaultMessage: 'Create space',
		description: 'Button label to create a new space from the empty state',
	},
});

const DoneIllustration = () => (
	<svg
		width="138"
		height="138"
		viewBox="0 0 138 138"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M105.615 79.3795C106.195 76.1295 106.495 72.6845 106.495 69.0245C106.495 65.3645 106.185 61.8245 105.595 58.5345C102.085 38.9645 88.485 26.9045 69.42 23.7995C66.165 23.2745 62.755 22.9995 59.21 22.9995C55.665 22.9995 51.955 23.2895 48.575 23.8595C29.5 27.0445 15.93 39.0795 12.405 58.5345C11.81 61.8195 11.5 65.3195 11.5 69.0245C11.5 72.7295 11.805 76.1295 12.385 79.3795C15.88 98.8145 29.465 110.87 48.57 114.055C51.95 114.62 55.505 114.915 59.205 114.915C62.905 114.915 66.165 114.645 69.415 114.115C88.51 111.015 102.115 98.9245 105.605 79.3795H105.615Z"
			fill="#82B536"
		/>
		<path
			d="M75.5152 68.6841L112.075 30.5991L125.595 43.1191L72.4902 88.7041L47.4902 65.6691L60.4902 50.6591L75.5102 68.6891L75.5152 68.6841Z"
			fill="#1868DB"
		/>
		<path
			d="M105.605 58.5395C104.565 52.7695 102.65 47.6395 99.975 43.2095L75.52 68.6845L60.5 50.6545L47.5 65.6645L72.5 88.6995L105.86 60.0645C105.785 59.5495 105.705 59.0395 105.615 58.5345L105.605 58.5395Z"
			fill="#09326C"
		/>
	</svg>
);

export interface RecommendedSpacesEmptyStateProps {
	testId?: string;
	onCreateSpace?: () => void;
}

export const RecommendedSpacesEmptyState = ({
	testId,
	onCreateSpace,
}: RecommendedSpacesEmptyStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container} testId={testId}>
			<Stack alignInline="center" space="space.150">
				<Box xcss={styles.illustration}>
					<DoneIllustration />
				</Box>
				<Heading size="small">{formatMessage(messages.heading)}</Heading>
				<Text size="small" color="color.text.subtlest" align="center">
					{formatMessage(messages.body)}
				</Text>
				{onCreateSpace && (
					<Button appearance="primary" iconBefore={AddIcon} onClick={onCreateSpace}>
						{formatMessage(messages.createSpace)}
					</Button>
				)}
			</Stack>
		</Box>
	);
};
